import { Link } from 'react-router-dom';
import { getStudentImage } from '../../common/styles/Images';
import InitialsAvatar, { hasProfilePicture } from '../../library/SimpleControls/InitialsAvatar';
import './StoryForm.css';

type StoryFile = {
    url: string;
    name?: string | null;
};

type StoryPerson = {
    name?: string;
    firstName?: string;
    lastName?: string;
    programName?: string | null;
    passoutYear?: string | number | null;
    profilePictureFilePath?: string | null;
    genderCode?: string | null;
};

type StoryPreviewCardProps = {
    whyNoteworthy?: string;
    narrativeHtml?: string;
    categories?: string[];
    markers?: string[];
    rating?: number | null;
    files?: StoryFile[];
    editHref?: string;
    showHeading?: boolean;
    narrator?: StoryPerson;
    protagonist?: StoryPerson;
};

function PersonCard({ role, person }: { role: string; person: StoryPerson }) {
    return (
        <div className="d-flex align-items-center gap-3 border rounded p-3 h-100 bg-primary bg-opacity-10 border-primary border-opacity-25">
            {hasProfilePicture(person.profilePictureFilePath) ? (
                <img
                    src={getStudentImage(person)}
                    alt={person.name || role}
                    className="rounded-circle border border-2 border-light flex-shrink-0 shadow-sm"
                    width={80}
                    height={80}
                />
            ) : (
                <InitialsAvatar firstName={person.firstName} lastName={person.lastName} name={person.name} size={80} />
            )}
            <div>
                <div className="text-uppercase text-primary-emphasis font-12 fw-semibold bg-primary-subtle d-inline-block py-1 px-3 rounded-pill">{role}</div>
                <div className="fw-semibold">{person.name || '—'}</div>
                <div className="d-flex flex-wrap gap-3 small text-muted mt-1">
                    <span><span className="fw-semibold text-body">Program:</span> {person.programName || '—'}</span>
                    <span><span className="fw-semibold text-body">Passout year:</span> {person.passoutYear || '—'}</span>
                </div>
            </div>
        </div>
    );
}

function attachmentName(file: StoryFile) {
    if (file.name) return file.name;
    const parts = String(file.url || '').split('?')[0].split('/');
    return decodeURIComponent(parts[parts.length - 1] || file.url);
}

function friendlyName(file: StoryFile) {
    const raw = attachmentName(file).replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i, '');
    const extension = (raw.match(/\.([a-z0-9]+)$/i) || [])[1] || '';
    const base = raw.replace(/\.[a-z0-9]+$/i, '').replace(/[_-]+/g, ' ').trim();
    if (!base) return extension ? extension.toUpperCase() : 'File';
    return extension ? `${base}.${extension.toLowerCase()}` : base;
}

function isImageFile(file: StoryFile) {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(attachmentName(file));
}

const StoryPreviewCard = ({ whyNoteworthy, narrativeHtml, categories = [], markers = [], rating, files, editHref, showHeading = true, narrator, protagonist }: StoryPreviewCardProps) => {
    const score = Number(rating);
    const hasRating = Number.isInteger(score) && score >= 1 && score <= 5;
    return (
        <div className="story-detail">
            {showHeading && (
                <div className="story-detail-heading">
                    <h2 className="story-detail-title">Story card</h2>
                    {editHref && (
                        <Link to={editHref} className="story-detail-edit" aria-label="Edit story">
                            <i className="icon-edit" />
                        </Link>
                    )}
                </div>
            )}
            <article className="story-detail-card">
                <header className="story-detail-top">
                    <p className="story-detail-headline">{whyNoteworthy || '—'}</p>
                    {hasRating ? (
                        <span className="story-stars story-detail-stars">
                            {[1, 2, 3, 4, 5].map((value) => (
                                <span key={value} className={score >= value ? 'story-star on static' : 'story-star static'}>★ {value}</span>
                            ))}
                        </span>
                    ) : (
                        <span className="story-detail-kicker">No rating</span>
                    )}
                </header>

                {(narrator || protagonist) && (
                    <div className="row g-3 mt-3">
                        {narrator && (
                            <div className={protagonist ? 'col-md-6' : 'col-12'}>
                                <PersonCard role="Narrator" person={narrator} />
                            </div>
                        )}
                        {protagonist && (
                            <div className={narrator ? 'col-md-6' : 'col-12'}>
                                <PersonCard role="Protagonist" person={protagonist} />
                            </div>
                        )}
                    </div>
                )}

                <section className="story-detail-section">
                    <div className="story-detail-kicker">Categories</div>
                    {categories.length ? (
                        <div className="story-detail-tags">
                            {categories.map((label) => <span key={label}>{label}</span>)}
                        </div>
                    ) : <div className="story-detail-empty">—</div>}
                </section>

                <section className="story-detail-section">
                    <div className="story-detail-kicker">Markers</div>
                    {markers.length ? (
                        <div className="story-detail-tags markers">
                            {markers.map((label) => <span key={label}>{label}</span>)}
                        </div>
                    ) : <div className="story-detail-empty">—</div>}
                </section>

                <div className="story-detail-narrative" dangerouslySetInnerHTML={{ __html: narrativeHtml || '<p>—</p>' }} />

                {files && files.length > 0 && (
                    <section className="story-detail-section">
                        <div className="story-detail-kicker">Attachments</div>
                        <ul className="story-attachments">
                            {files.map((file) => (
                                <li key={file.url}>
                                    <a href={file.url} target="_blank" rel="noreferrer">
                                        {isImageFile(file) && <img className="story-attachment-image" src={file.url} alt={friendlyName(file)} />}
                                        <span>{friendlyName(file)}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </article>
        </div>
    );
};

export default StoryPreviewCard;
